import {
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import Icon, { ICON_SIZE } from '../common/Icon';

import type {
  InfoPanelProps,
  InfoPanelItemProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/InfoPanelProps';

import STYLES from './InfoPanel.module.scss';

const InfoPanelItem = ({ icon, label, link }: InfoPanelItemProps) => {
  const IconComponent = Icon(icon, ICON_SIZE.SMALL);

  return (
    <BpkFlex gap={BpkSpacing.SM} data-testid="info-panel-item">
      {/* BpkVessel (not BpkBox/BpkFlex): InfoPanel.module.scss sets
          `svg { fill: tokens.$bpk-color-sky-blue }` on this element — a
          descendant selector targeting the icon's internal <svg>, which has
          no explicit `fill="currentColor"`, so a BpkBox/BpkFlex `color` prop
          would not reach it. See
          backpack-api-gaps/WOM-6779-accordion-infopanel.md. */}
      <BpkVessel as="div" className={STYLES.Icon}>
        <IconComponent />
      </BpkVessel>
      <BpkFlex grow={1} basis="0%" color="text-secondary">
        {link ? (
          <BpkLink href={link}>{label}</BpkLink>
        ) : (
          <BpkText>{label}</BpkText>
        )}
      </BpkFlex>
    </BpkFlex>
  );
};

export default function InfoPanel({
  leftColumnItems,
  rightColumnItems,
  title,
}: InfoPanelProps) {
  return (
    <BpkBox display="flex" flexDirection="column" gap={BpkSpacing.Base}>
      {/* BpkVessel (not BpkBox/BpkFlex): InfoPanel.module.scss carries a
          `h3 { @include breakpoints.bpk-breakpoint-mobile { ... } }`
          descendant-selector rule that responsively overrides the title's
          typography. BpkText.textStyle only accepts a single non-responsive
          value (verified against BpkText.d.ts), so this breakpoint-driven
          swap cannot be expressed via a Backpack prop. See
          backpack-api-gaps/WOM-6779-accordion-infopanel.md. */}
      <BpkVessel as="div" className={STYLES.Title}>
        <BpkText tagName="h3" textStyle={TEXT_STYLES.editorial2}>
          {title}
        </BpkText>
      </BpkVessel>
      <BpkFlex
        direction={{ base: 'column', [BpkBreakpoint.Tablet]: 'row' }}
        gap={{ base: BpkSpacing.MD, [BpkBreakpoint.Tablet]: BpkSpacing.LG }}
      >
        {leftColumnItems && leftColumnItems.length > 0 && (
          <BpkFlex
            direction="column"
            grow={1}
            basis="0%"
            gap={BpkSpacing.MD}
            data-testid="info-panel"
          >
            {leftColumnItems.map((item) => (
              <InfoPanelItem key={item.label} {...item} />
            ))}
          </BpkFlex>
        )}
        {rightColumnItems && rightColumnItems.length > 0 && (
          <BpkFlex
            direction="column"
            grow={1}
            basis="0%"
            gap={BpkSpacing.MD}
            data-testid="info-panel"
          >
            {rightColumnItems.map((item) => (
              <InfoPanelItem key={item.label} {...item} />
            ))}
          </BpkFlex>
        )}
      </BpkFlex>
    </BpkBox>
  );
}
